"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import Swal from "sweetalert2";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { GATED_FEATURES, type GatedFeature } from "@/components/SignInRequired";

/**
 * Guard for the actions that belong to a signed-in-only feature — adding a
 * favourite, opening the Recommended or Favourites tab (requirement 3).
 *
 * `requireSignIn` returns true when the action may go ahead. When it may not,
 * it explains why and offers to take the user to that feature's page (or an
 * optional sign-in destination).
 */
export function useSignInGate() {
  const { status } = useAuth();
  const { translate } = useLanguage();
  const router = useRouter();

  const isSignedIn = status === "authenticated";

  const requireSignIn = useCallback(
    async (feature: GatedFeature, redirectTo?: string): Promise<boolean> => {
      if (isSignedIn) return true;
      // Firebase has not reported yet — say nothing rather than tell a
      // signed-in user they are signed out.
      if (status === "loading") return false;

      const result = await Swal.fire({
        title: translate("signInRequired"),
        text: `${translate("featureLoggedInOnly")} ${translate(
          GATED_FEATURES[feature].description
        )}`,
        icon: "info",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: translate("signInToContinue"),
        cancelButtonText: translate("cancel"),
      });

      if (result.isConfirmed) {
        router.push(redirectTo ?? `/${feature}`);
      }
      return false;
    },
    [isSignedIn, status, router, translate]
  );

  return { isSignedIn, requireSignIn };
}
